import {
  type ErrorType,
  getApiInspectionsV1CompaniesListQueryKey,
  type InspectionCompany,
  type Inspector,
  isApiFormError,
  isFieldErrorResponse,
  useApiInspectionsV1InspectorsCreate,
  useApiInspectionsV1InspectorsUpdate,
  useQueryClient,
} from 'api';
import { useNotification } from 'features';
import { CheckboxField, EmailField, Form, PhoneNumberField, TextField, useForm } from 'form';
import { useTranslation } from 'i18n';
import { ActionRow, Button, Typography } from 'ui';

type FormValues = {
  name: string;
  email?: string;
  phone_number?: string;
  company: number;
  is_active: boolean;
};

type BaseProps = {
  onClose?: () => void;
  onSave?: () => void;
  company: InspectionCompany;
};

type CreateProps = BaseProps & {
  action: 'create';
  inspector?: never;
};

type EditProps = BaseProps & {
  action: 'edit';
  inspector: Inspector;
};
const isEditMode = (props: CreateProps | EditProps): props is EditProps => {
  return props.action === 'edit';
};
export const InspectorMutate = (props: CreateProps | EditProps) => {
  const { t } = useTranslation('orders');
  const { open } = useNotification();
  const { company, action = 'create', inspector, onSave, onClose } = props;
  const queryClient = useQueryClient();
  const form = useForm<FormValues>({
    defaultValues: {
      company: company.id,
      email: inspector?.email ?? '',
      is_active: inspector?.is_active ?? true,
      name: inspector?.name ?? '',
      phone_number: inspector?.phone_number,
    },
  });

  const handleSuccess = () => {
    onSave?.();
    form.reset();
    queryClient.invalidateQueries({
      queryKey: getApiInspectionsV1CompaniesListQueryKey(),
    });
  };

  const handleError = (error: ErrorType<unknown>) => {
    if (isApiFormError(error)) {
      open({
        description: error?.response?.data.detail,
        message: t('inspectionCompany.notifications.errorMutatingInspector'),
        type: 'error',
      });
      return;
    }
    if (isFieldErrorResponse(error)) {
      Object.keys(error.response?.data).forEach((field) => {
        form.setError(field as keyof FormValues, { message: error.response.data[field] });
      });
      if (error.response?.data.non_field_errors) {
        open({
          description: error?.response?.data.non_field_errors[0],
          message: t('inspectionCompany.notifications.errorMutatingInspector'),
          type: 'error',
        });
      }
      return;
    }

    open({
      message: t('inspectionCompany.notifications.errorMutatingInspector'),
      type: 'error',
    });
  };

  const { mutate: create, isPending: isCreatePending } = useApiInspectionsV1InspectorsCreate({
    mutation: {
      onError: handleError,
      onSuccess: handleSuccess,
    },
  });

  const { mutate: edit, isPending: isEditPending } = useApiInspectionsV1InspectorsUpdate({
    mutation: {
      onError: handleError,
      onSuccess: handleSuccess,
    },
  });

  const handleSubmit = (values: FormValues) => {
    if (isEditMode(props)) {
      edit({
        data: values,
        id: props.inspector.id,
      });
      return;
    }
    create({
      data: values,
    });
  };

  const handleCancel = () => {
    onClose?.();
  };

  return (
    <Form {...form} onSubmit={handleSubmit}>
      <Typography variant="h6">
        {action === 'create'
          ? t('inspectionCompany.formTitles.addInspector')
          : t('inspectionCompany.formTitles.editInspector')}
      </Typography>
      <TextField
        label={t('inspectionCompany.fields.name')}
        name="name"
        required
        size="small"
        data-testid="inspector-name"
      />
      <EmailField label={t('inspectionCompany.fields.email')} name="email" size="small" data-testid="inspector-email" />
      <PhoneNumberField label={t('inspectionCompany.fields.phoneNumber')} name="phone_number" size="small" />

      <CheckboxField name="is_active" label={t('inspectionCompany.fields.active')} />

      <ActionRow>
        <Button fullWidth variant="secondary" onClick={handleCancel}>
          {t('inspectionCompany.actions.cancel')}
        </Button>
        <Button
          fullWidth
          data-testid="inspector-save-btn"
          type="submit"
          variant="primary"
          disabled={isCreatePending || isEditPending}
        >
          {t('inspectionCompany.actions.save')}
        </Button>
      </ActionRow>
    </Form>
  );
};
